import React from 'react';
import { StyleProp, View, ViewStyle, Image, ImageProps } from 'react-native';
import Skeleton from '../Skeleton';
import { colors } from '../../../utils/theme';
import Text, { TextVariant } from '../Text';
import { useAvatar } from '../../../hooks/useAvatar';
import Icon, { IconName } from '../Icon';
import SkeletonLoadingContainer from '../SkeletonLoadingContainer';
import useMedicalProfile from '../../../hooks/api/useMedicalProfile';
import urls from '../../../utils/urls';
import getName from '../../../utils/getName';

export type FirstLastName = {
    firstName: string;
    lastName: string;
    preferredName?: string;
    preferredFirstName?: string;
};

export type ExplicitFallback = {
    fallbackName: string;
};

export type AvatarDisplayFallback = FirstLastName | ExplicitFallback;

export type Size = 'sm' | 'md' | 'xs' | 'xl' | 'xxl' | 'lg' | 'profile-header' | 'drawer-avatar';

export const getFallbackFromName = (firstName?: string | null, lastName?: string | null) => {
    // get initials capitalized from first and last name
    const firstInitial = (firstName || '').charAt(0)?.toUpperCase() || '';
    const lastInitial = (lastName || '').charAt(0)?.toUpperCase() || '';

    return `${firstInitial}${lastInitial}`;
};

/**
 * Fallback for any generic string.  Will return the first letter of the string capitalized capped at 3 words.
 */
export const getFallbackForGenericString = (text?: string | null) => {
    return (text || '')
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase())
        .join('')
        .substring(0, 3);
};

const sizeMap: Record<Size, number> = {
    xs: 30,
    sm: 33,
    md: 50,
    lg: 80,
    xl: 92,
    xxl: 131,
    'profile-header': 64,
    'drawer-avatar': 27,
};

const sizeTextVariantMap: Record<Size, TextVariant> = {
    xs: 'avatar-fallback-xs',
    sm: 'avatar-fallback-sm',
    md: 'h3',
    lg: 'h2',
    xl: 'h2',
    xxl: 'h1',
    'profile-header': 'h3',
    'drawer-avatar': 'body-2',
};

type ExplicitDimensions = {
    width: number;
    height: number;
};

export type AvatarOwnProps = {
    isLoading: boolean;
    image: string | null;
    size: Size | ExplicitDimensions;
    style?: StyleProp<ViewStyle>;
} & (AvatarDisplayFallback | {});

export type AvatarProps = AvatarOwnProps;

const Avatar: React.FC<AvatarProps> = (props) => {
    const { image, size } = props;
    const hasExplicitDimensions = typeof size === 'object';
    const getDimensions = (): ExplicitDimensions => {
        if (hasExplicitDimensions) return size;

        return { width: sizeMap[size], height: sizeMap[size] };
    };

    const fallbackName = React.useMemo(() => {
        if ('fallbackName' in props) return props.fallbackName;

        if ('firstName' in props)
            return getFallbackFromName(
                props.preferredFirstName || props.preferredName || props.firstName,
                props.lastName,
            );

        return '';
    }, [props]);

    const sharedContainerStyles: StyleProp<ViewStyle> = {
        ...getDimensions(),
        borderRadius: hasExplicitDimensions ? 5 : getDimensions().height / 2,
    };

    if (props.isLoading) {
        return <Skeleton style={[sharedContainerStyles, props.style]} />;
    }

    const renderInContainer = (children: React.ReactNode) => (
        <View
            style={[
                sharedContainerStyles,
                {
                    backgroundColor: colors['Grey-Light'],
                    borderColor: colors['Avatar-border'],
                    borderWidth: size === 'xl' ? 2 : 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    overflow: 'hidden',
                },
                props.style,
            ]}
        >
            {children}
        </View>
    );

    if (!image) {
        return renderInContainer(
            <Text
                testID="avatar-fallback"
                variant={hasExplicitDimensions ? 'h3' : sizeTextVariantMap[size]}
                color="White"
            >
                {fallbackName}
            </Text>,
        );
    } else {
        return renderInContainer(
            renderInContainer(
                <Image
                    testID="avatar"
                    source={{ uri: image }}
                    style={{
                        height: '100%',
                        width: '100%',
                    }}
                    resizeMode="cover"
                />,
            ),
        );
    }
};

export default React.memo(Avatar);

export type LoadableAvatarProps = Omit<AvatarProps, 'isLoading' | 'image'> & {
    avatarUrl: string | null;
} & AvatarDisplayFallback;
export const LoadableAvatar: React.FC<LoadableAvatarProps> = (props) => {
    const { avatarUrl, ...rest } = props;
    const { isLoadingAvatar, avatar } = useAvatar({ avatarAPIUrl: avatarUrl || null });

    return <Avatar isLoading={!!avatarUrl && isLoadingAvatar} image={avatar?.base64 || null} {...rest} />;
};

export type LoadingAvatarPlaceholderProps = {
    size: Size;
};

export const LoadingAvatarPlaceholder: React.FC<LoadingAvatarPlaceholderProps> = ({ size }) => {
    return (
        <View
            style={{
                width: sizeMap[size],
                height: sizeMap[size],
                borderRadius: sizeMap[size] / 2,
                backgroundColor: colors['Grey-Light'],
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            <Skeleton style={{ width: sizeMap[size], height: sizeMap[size] }} />
        </View>
    );
};

export type IconAvatarProps = {
    icon: IconName;
    size?: Size;
};

export const IconAvatar: React.FC<IconAvatarProps> = (props) => {
    const sharedContainerStyles: StyleProp<ViewStyle> = {
        height: sizeMap[props.size || 'md'],
        width: sizeMap[props.size || 'md'],
        borderRadius: sizeMap[props.size || 'md'] / 2,
    };

    return (
        <View
            style={[
                sharedContainerStyles,
                {
                    backgroundColor: colors['Grey-Light'],
                    borderColor: colors['Avatar-border'],
                    borderWidth: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    overflow: 'hidden',
                },
            ]}
        >
            <Icon size="avatar-icon" name={props.icon} color="White" />
        </View>
    );
};

export type AvatarPlaceholderProps = { text: string; size: Size };

export const AvatarPlaceholder: React.FC<AvatarPlaceholderProps> = ({ size, text }) => {
    return (
        <View
            style={{
                width: sizeMap[size],
                height: sizeMap[size],
                borderRadius: sizeMap[size] / 2,
                backgroundColor: colors['Grey-Light'],
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderColor: colors['Avatar-border'],
                borderStyle: 'dashed',
                borderWidth: 2,
            }}
        >
            <Text variant={sizeTextVariantMap[size]} color="White">
                {text}
            </Text>
        </View>
    );
};

export type GenericAvatarStackItem = {
    avatarAPIUrl: string;
    fallbackName: string;
    type: 'generic';
};

export type MedicalProfileAvatarStackItem = {
    medicalProfileId: string;
    type: 'medicalProfile';
};

export type AvatarStackItem = GenericAvatarStackItem | MedicalProfileAvatarStackItem;

export type AvatarStackProps = {
    style?: StyleProp<ViewStyle>;
    items: AvatarStackItem[];
    displayLimit: number;
};

const MedicalProfileAvatarStackItem: React.FC<{ medicalProfileId: string }> = (props) => {
    const { profile, isLoadingProfile } = useMedicalProfile({ medicalProfileId: props.medicalProfileId });
    const { isLoadingAvatar, avatar } = useAvatar({
        avatarAPIUrl: urls.api.medicalProfiles.avatar(props.medicalProfileId),
    });

    return (
        <Avatar
            size="sm"
            isLoading={isLoadingProfile || isLoadingAvatar}
            image={avatar?.base64 || null}
            fallbackName={profile?.contactDetails ? getFallbackForGenericString(getName(profile?.contactDetails)) : ''}
        />
    );
};

export const AvatarStack: React.FC<AvatarStackProps> = (props) => {
    const { items, displayLimit } = props;
    const containerStyle: StyleProp<ViewStyle> = {
        width: 28,
        overflow: 'visible',
    };

    return (
        <View
            style={[
                props.style,
                {
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    flexDirection: 'row',
                },
            ]}
            collapsable={false}
        >
            {items.slice(0, displayLimit).map((item, index) =>
                item.type === 'medicalProfile' ? (
                    <View style={containerStyle} key={item.medicalProfileId}>
                        <MedicalProfileAvatarStackItem medicalProfileId={item.medicalProfileId} />
                    </View>
                ) : (
                    <View style={containerStyle} key={index}>
                        <LoadableAvatar size="sm" avatarUrl={item.avatarAPIUrl} fallbackName={item.fallbackName} />
                    </View>
                ),
            )}
            {items.length > displayLimit && <AvatarPlaceholder size="sm" text={`+${items.length - displayLimit}`} />}
        </View>
    );
};

export const UnstyledAvatar: React.FC<
    { avatarUrl: string; width: number; height: number } & Omit<ImageProps, 'source'>
> = (props) => {
    const { avatarUrl, width, height, ...rest } = props;
    const { isLoadingAvatar, avatar } = useAvatar({ avatarAPIUrl: avatarUrl || null });
    const isLoading = !!avatarUrl && isLoadingAvatar;
    return (
        <SkeletonLoadingContainer isLoading={isLoading}>
            {isLoading ? (
                <View style={{ width, height }} />
            ) : (
                <Image source={{ uri: avatar?.base64 || '' }} style={[{ width, height }, rest.style]} {...rest} />
            )}
        </SkeletonLoadingContainer>
    );
};
